import React, { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { AppButton, Box, Text } from '@common-fe/common-fe';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';

import ScanningDocs from '@/assets/icons/scanning-docs.json';
import useProgress from '@/components/controls/DropScan/hooks/useProgress';
import { InnerProgressBar, ProgressBar } from '@/components/controls/DropScan/ProcessScan.styles';

interface Props {
  onCancelScanning: () => void;
  isMobile?: boolean;
}

const ScanFileContainer: React.FC<Props> = ({ onCancelScanning, isMobile }) => {
  const { t } = useTranslation();
  const { progress, setProgress } = useProgress();
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const lottieModalRef = useRef<LottieRefCurrentProps>(null);

  useEffect(() => {
    if (lottieRef.current) {
      lottieRef.current.setSpeed(0.8);
    }
    if (lottieModalRef.current) {
      lottieModalRef.current.setSpeed(0.8);
    }
  }, []);

  const handleCancelScanning = useCallback(() => {
    setProgress(0);
    onCancelScanning();
  }, [setProgress, onCancelScanning]);

  return (
    <Box data-testId="scan-file_wrapper" flex="grow">
      <Box
        pad="spacing12"
        round="container1Round"
        background="module"
        {...(isMobile ? {} : { flex: 'grow' })}
      >
        <Box
          pad="spacing24"
          round="container1Round"
          background="canvas"
          align="center"
          justify="center"
          border={{ size: '1px', color: 'border2' }}
          {...(isMobile ? {} : { flex: 'grow' })}
        >
          <Box width={{ width: '100%', max: '400px' }} align="center">
            <Lottie
              lottieRef={lottieRef}
              loop
              data-testId="scan-file_wrapper_scanning-docs-icon"
              animationData={ScanningDocs}
              height={128}
              width={128}
            />
            <Text data-testId="scan-file_wrapper_title" margin={{ top: 'spacing24' }} weight={700}>
              {t('Hang tight while we scan your document(s)…')}
            </Text>
            <ProgressBar
              data-testId="scan-file_wrapper_progress-bar"
              margin={{ vertical: 'spacing12' }}
              width="100% !important"
            >
              <InnerProgressBar progress={progress} />
            </ProgressBar>
            <Text>{`${Math.floor(progress * 100)}% Scanned`}</Text>
          </Box>
        </Box>
      </Box>

      <Box margin={{ top: 'spacing24' }} direction="row" gap="spacing12" justify="end">
        <Box width={{ max: isMobile ? '100%' : '160px', width: '100%' }}>
          <AppButton
            testId="scan-file-cancel"
            width="100%"
            color="red"
            buttonType="tertiary"
            onClick={handleCancelScanning}
          >
            {t('Cancel Scanning')}
          </AppButton>
        </Box>
      </Box>
    </Box>
  );
};

export default ScanFileContainer;
